import * as Sentry from "@sentry/svelte";
import { getStore } from "./store.svelte.js";
import { requestJSON } from "./payment.js";

const baseURL = import.meta.env.VITE_BASE_URL;
const NODE_HTML_URL = "https://hotspot.toomuchwifi.co.za/node.html";

const store = getStore();

const HOTSPOT_NODE_HTML = {
  identity: "node-0401",
  "logged-in": "no",
  username: "",
  mac: "62:B7:9B:07:74:BA",
};

if (import.meta.env.MODE === "production") {
  console.log("We are in production mode");
} else if (import.meta.env.MODE === "development") {
  console.log("We are in development mode");
}

let env = { MODE: import.meta.env.MODE }; // Replace with your actual env variable if necessary

/** @type {ReturnType<typeof setTimeout> | null} */
let loadingIndicatorTimer = null;

export function loadingStart() {
  // Wait 120ms before displaying loading indicator, makes apps feel faster
  if (loadingIndicatorTimer) {
    clearTimeout(loadingIndicatorTimer);
  }
  loadingIndicatorTimer = setTimeout(() => {
    store.loadingIndicator = true;
    loadingIndicatorTimer = null;
  }, 120);
}

export function loadingStop() {
  if (loadingIndicatorTimer) {
    clearTimeout(loadingIndicatorTimer);
    loadingIndicatorTimer = null;
  }
  store.loadingIndicator = false;
}

const NODE_HTML_TIMEOUT_MS = 7000;
const NODE_HTML_EVENT_SAMPLE_RATE = 0.01;
const VOUCHER_MISSING_MAC_EVENT_SAMPLE_RATE = 0.01;

/** At most one Sentry event per issue key per page load — breadcrumbs still always fire. */
const reportedNodeHtmlIssues = new Set();
let reportedVoucherMissingMac = false;

function nodeHtmlClientContext() {
  const connection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;

  return {
    browser_online: navigator.onLine,
    page_protocol: window.location.protocol,
    connection_type: connection?.effectiveType ?? null,
    downlink_mbps: connection?.downlink ?? null,
    rtt_ms: connection?.rtt ?? null,
  };
}

function logNodeHtmlIssue(message, { level = "warning", extra = {} } = {}) {
  const context = {
    url: NODE_HTML_URL,
    timeout_ms: NODE_HTML_TIMEOUT_MS,
    sample_rate: NODE_HTML_EVENT_SAMPLE_RATE,
    ...nodeHtmlClientContext(),
    ...extra,
  };

  Sentry.addBreadcrumb({
    category: "hotspot.node_html",
    message,
    level,
    data: context,
  });

  // Expected hotspot failures are high-volume. Keep breadcrumbs for every
  // occurrence, but send only one sampled event per issue type per page load.
  if (
    reportedNodeHtmlIssues.has(message) ||
    Math.random() >= NODE_HTML_EVENT_SAMPLE_RATE
  ) {
    return;
  }
  reportedNodeHtmlIssues.add(message);

  Sentry.withScope((scope) => {
    scope.setTag("hotspot", "node_html");
    scope.setLevel(level);
    scope.setContext("node_html", context);
    // Prefer messages over exceptions so these group separately from real bugs.
    Sentry.captureMessage(message);
  });
}

export function logVoucherMissingMac({
  voucherVendor,
  isConnectedToHotspot,
  hasStoredMac,
  node,
}) {
  const message = "Voucher submission blocked: MAC unavailable";
  const context = {
    voucher_vendor: voucherVendor,
    is_connected_to_hotspot: isConnectedToHotspot,
    has_stored_mac: hasStoredMac,
    node: node || null,
    sample_rate: VOUCHER_MISSING_MAC_EVENT_SAMPLE_RATE,
    ...nodeHtmlClientContext(),
  };

  Sentry.addBreadcrumb({
    category: "hotspot.voucher",
    message,
    level: "warning",
    data: context,
  });

  if (
    reportedVoucherMissingMac ||
    Math.random() >= VOUCHER_MISSING_MAC_EVENT_SAMPLE_RATE
  ) {
    return;
  }
  reportedVoucherMissingMac = true;

  Sentry.withScope((scope) => {
    scope.setTag("hotspot", "voucher_missing_mac");
    scope.setTag("voucher_vendor", voucherVendor || "unknown");
    scope.setTag(
      "hotspot_connected",
      String(Boolean(isConnectedToHotspot)),
    );
    scope.setTag("stored_mac_present", String(Boolean(hasStoredMac)));
    scope.setTag("hotspot_node", node || "unknown");
    scope.setLevel("warning");
    scope.setContext("voucher_submission", context);
    Sentry.captureMessage(message);
  });
}

export async function getHotspotData() {
  const store = getStore();
  console.log("Utils:Store:getHotspotData", store);
  console.log("getHotspotData");
  console.log("env.MODE", env.MODE);

  if (env.MODE === "development") {
    store.isConnectedToHotspot = true;
    store.macAddress = HOTSPOT_NODE_HTML.mac;
    store.node = HOTSPOT_NODE_HTML.identity ?? null;
    store.loading = false;
    console.log("store.macAddress", store.macAddress);
  } else {
    const controller = new AbortController();
    let requestTimedOut = false;
    const startedAt = Date.now();
    const timeoutId = setTimeout(
      () => {
        requestTimedOut = true;
        controller.abort(
          new DOMException("Hotspot data request timed out", "TimeoutError"),
        );
      },
      NODE_HTML_TIMEOUT_MS,
    );
    try {
      Sentry.addBreadcrumb({
        category: "hotspot",
        message: "Fetching node.html",
        level: "info",
        data: { url: NODE_HTML_URL, timeout_ms: NODE_HTML_TIMEOUT_MS },
      });

      const response = await fetch(NODE_HTML_URL, {
        method: "GET",
        credentials: "omit", // Equivalent to withCredentials: false
        signal: controller.signal,
      });

      const elapsedMs = Date.now() - startedAt;

      if (!response.ok) {
        logNodeHtmlIssue("node.html returned non-OK status", {
          extra: {
            status: response.status,
            elapsed_ms: elapsedMs,
          },
        });
        store.isConnectedToHotspot = false;
        store.isAuthenticatedOnHotspot = false;
        store.loading = false;
        return;
      }

      const data = await response.json();
      if (!response.ok || !/^([0-9a-f]{2}:){5}[0-9a-f]{2}$/i.test(data?.mac || "")) {
        throw new Error("Invalid hotspot device response");
      }

      store.isConnectedToHotspot = true;
      const loggedInStatus = data["logged-in"];
      store.isAuthenticatedOnHotspot = loggedInStatus === "yes";

      const mac = typeof data.mac === "string" ? data.mac.trim() : data.mac;
      const identity =
        typeof data.identity === "string" ? data.identity.trim() : data.identity;

      if (mac) {
        store.macAddress = mac;
      }

      store.node = identity || null;

      if (!mac || !identity) {
        logNodeHtmlIssue("node.html missing mac or identity", {
          extra: {
            elapsed_ms: elapsedMs,
            has_mac: Boolean(mac),
            has_identity: Boolean(identity),
            logged_in: loggedInStatus ?? null,
            raw_keys: data && typeof data === "object" ? Object.keys(data) : [],
          },
        });
      }

      store.loading = false;
    } catch (error) {
      const elapsedMs = Date.now() - startedAt;
      if (requestTimedOut) {
        logNodeHtmlIssue("node.html timed out", {
          level: "warning",
          extra: {
            elapsed_ms: elapsedMs,
            timed_out: true,
          },
        });
      } else {
        console.error(error);
        logNodeHtmlIssue("node.html fetch failed", {
          level: "warning",
          extra: {
            elapsed_ms: elapsedMs,
            timed_out: false,
            error_name: error?.name ?? null,
            error_message: error?.message ?? String(error),
          },
        });
      }
      // Keep last-known MAC/node on transient fetch failures so redeem/pay
      // still have a device identity if the user was previously on-hotspot.
      store.isConnectedToHotspot = false;
      store.isAuthenticatedOnHotspot = false;
      store.loading = false;
    } finally {
      clearTimeout(timeoutId);
    }
  }
}

export async function getActiveAllocation(cellphone) {
  try {
    return await requestJSON(`${baseURL}allocation/active/`, {
      method: "POST",
      credentials: "include",
      timeoutMs: 8000,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ cellphone: cellphone }),
    });

  } catch (error) {
    console.error("Error:", error);
    return null;
  }
}

export async function getAccount(cellphone) {
  try {
    return await requestJSON(`${baseURL}hotspot-account/info/`, {
      method: "POST",
      credentials: "include",
      timeoutMs: 8000,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ cellphone: cellphone }),
    });

  } catch (error) {
    console.log("Error:", error);
    return null;
  }
}

export async function getCellphoneByMAC(macAddress) {
  if (!macAddress) {
    return null;
  }
  try {
    return await requestJSON(`${baseURL}get-cellphone/${macAddress}/`, {
      method: "GET",
      timeoutMs: 8000,
      headers: {
        "Content-Type": "application/json",
      },
    });

  } catch (error) {
    console.log("Error:", error);
    return null;
  }
}
